/**
 * Users tab content for the Admin Dashboard.
 *
 * Self-contained presentational component — receives all state and
 * handlers as props. No fetching or modal state lives here; the parent
 * (AdminDashboard.jsx) owns those so we can swap this component in/out
 * without ripping the whole admin dashboard apart.
 *
 * Extracted from AdminDashboard.jsx for maintainability. No behavior
 * changes vs. the inline JSX.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Plus, Download, RefreshCw, Mail, PenLine, Trash2, Pencil,
  Gift, Ban, KeyRound, Megaphone,
} from 'lucide-react';
import { formatRelativeShort, relativeColorClass } from '@/lib/timeFormat';
import { useAdminDashboard } from '@/context/AdminDashboardContext';

export function UsersTabContent() {
  const {
    users,
    loading,
    currentUser,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    setShowCreateModal,
    handleExport,
    setEditUser,
    setPasswordResetUser,
    handleInviteContributor,
    setDeleteUser,
    setDirectPasswordUser,
    setShowDirectPasswordModal,
    handleMakeFree,
    handleRevokeFree,
    getStatusColor,
    getPaymentMethod,
    setShowReengagementModal,
  } = useAdminDashboard();
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle>User Management</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-48"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateModal(true)} size="sm" data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
            <Button onClick={handleExport} variant="outline" size="sm" data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={() => setShowReengagementModal?.(true)}
              variant="outline"
              size="sm"
              className="border-[#00E5CC]/40 text-[#00b3a0] hover:bg-[#00E5CC]/10 dark:text-[#00E5CC]"
              data-testid="bulk-reachout-btn"
              title="Send 'we miss you' email to dormant users"
            >
              <Megaphone className="h-4 w-4 mr-2" />
              Bulk Reach-Out
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400 dark:text-gray-500" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">No users found</p>
          ) : (
            users.map((u) => (
              <Card
                key={u.user_id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setEditUser(u)}
                data-testid={`user-card-${u.user_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{u.name || u.email.split('@')[0]}</p>
                      <p className="text-sm text-gray-500 truncate">{u.email}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge className={`${getStatusColor(u.subscription_status)} text-white text-xs`}>
                          {u.subscription_status}
                        </Badge>
                        {u.is_admin && <Badge variant="destructive" className="text-xs">Admin</Badge>}
                        {u.is_contributor && <Badge className="bg-blue-500 text-white text-xs">Contributor</Badge>}
                        {u.is_gifted && <Badge className="bg-purple-500 text-white text-xs">Gifted</Badge>}
                        {u.last_reachout_sent_at && (
                          <Badge
                            variant="outline"
                            className="text-xs border-[#00E5CC]/40 text-[#00b3a0] dark:text-[#00E5CC]"
                            title={`Last re-engagement email: ${new Date(u.last_reachout_sent_at).toLocaleString()}`}
                            data-testid={`user-reached-out-${u.user_id}`}
                          >
                            Reached out {formatRelativeShort(u.last_reachout_sent_at)}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); setPasswordResetUser(u); }}
                        title="Send password reset"
                      >
                        <Mail className="h-4 w-4 text-blue-500" />
                      </Button>
                      {!u.is_contributor && !u.is_admin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleInviteContributor(u); }}
                          title="Invite as contributor"
                          data-testid={`invite-contributor-${u.user_id}`}
                        >
                          <PenLine className="h-4 w-4 text-[#00E5CC]" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); setDeleteUser(u); }}
                        disabled={u.email === currentUser?.email}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
                    <span
                      className={relativeColorClass(u.last_seen_at)}
                      title={u.last_seen_at ? new Date(u.last_seen_at).toLocaleString() : 'Never'}
                      data-testid={`user-last-seen-${u.user_id}`}
                    >
                      {u.last_seen_at ? `Active ${formatRelativeShort(u.last_seen_at)}` : 'Never active'}
                    </span>
                    <span>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Accounts Linked</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400 dark:text-gray-500" />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow
                    key={u.user_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setEditUser(u)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{u.email}</p>
                        {u.last_reachout_sent_at && (
                          <span
                            className="inline-flex items-center mt-1 text-[10px] uppercase tracking-wide text-[#00b3a0] dark:text-[#00E5CC]"
                            title={`Last re-engagement email: ${new Date(u.last_reachout_sent_at).toLocaleString()}`}
                            data-testid={`user-reached-out-${u.user_id}`}
                          >
                            • Reached out {formatRelativeShort(u.last_reachout_sent_at)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(u.subscription_status)} text-white`}>
                        {u.subscription_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getPaymentMethod(u).color} text-white`}>
                        {getPaymentMethod(u).label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {u.is_admin ? (
                        <Badge variant="destructive">Admin</Badge>
                      ) : (
                        <Badge variant="secondary">User</Badge>
                      )}
                    </TableCell>
                    {/* Accounts linked column. We surface the primary
                        auth_provider plus any entries in linked_providers
                        (set when the user connects a second OAuth account
                        from settings — feature on the backlog). For users
                        signed up via plain email this just shows "Email". */}
                    <TableCell className="capitalize">
                      {(() => {
                        const primary = u.auth_provider || 'email';
                        const linked = Array.isArray(u.linked_providers) ? u.linked_providers : [];
                        const all = Array.from(new Set([primary, ...linked]));
                        return (
                          <div className="flex flex-wrap gap-1">
                            {all.map((p) => (
                              <Badge key={p} variant="outline" className="capitalize text-[10px] px-1.5 py-0">
                                {p}
                              </Badge>
                            ))}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell
                      className={relativeColorClass(u.last_seen_at)}
                      title={u.last_seen_at
                        ? `Last seen ${new Date(u.last_seen_at).toLocaleString()}\nViews: ${u.phrases_viewed || 0}`
                        : 'Never active'}
                      data-testid={`user-last-seen-${u.user_id}`}
                    >
                      {formatRelativeShort(u.last_seen_at)}
                    </TableCell>
                    <TableCell>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1 flex-wrap">
                        {u.is_gifted ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRevokeFree(u.user_id)}
                            title="Revoke gifted subscription"
                          >
                            <Ban className="h-4 w-4 text-orange-500" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleMakeFree(u.user_id)}
                            title="Gift free subscription"
                            disabled={u.is_admin}
                          >
                            <Gift className="h-4 w-4 text-purple-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setPasswordResetUser(u)}
                          title="Send password reset email"
                        >
                          <Mail className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDirectPasswordUser(u);
                            setShowDirectPasswordModal(true);
                          }}
                          title="Set password directly"
                        >
                          <KeyRound className="h-4 w-4 text-green-500" />
                        </Button>
                        {!u.is_contributor && !u.is_admin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleInviteContributor(u)}
                            title="Invite as contributor (sets is_contributor=true and emails them)"
                            data-testid={`invite-contributor-row-${u.user_id}`}
                          >
                            <PenLine className="h-4 w-4 text-[#00E5CC]" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditUser(u)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteUser(u)}
                          disabled={u.email === currentUser?.email}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export default UsersTabContent;
